// src/services/apaleo/city-tax.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { ReservationModel, FolioModel, CityTaxAuditRecord, ToolUser, LogFn } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, ALLOWED_PROPERTIES } from './config';

const part: ThisType<ApaleoClient> = {
async fetchCityTaxAuditData(
  from: string,
  to: string,
  propertyFilter = "All",
): Promise<CityTaxAuditRecord[]> {
  if (IS_DEV) {
    console.log(`[ApaleoAuth] Generating mock data for City Tax Audit from ${from} to ${to} (Property: ${propertyFilter})`);

    const propertiesToUse: string[] = propertyFilter === "All" ? ALLOWED_PROPERTIES : [propertyFilter];

    // Create realistic mock records that span across the selected properties
    const mockRecords: CityTaxAuditRecord[] = [];

    // 1. Bezahlt (Closed folios, fully paid)
    mockRecords.push({
      reservationId: "RES-MOCK-PAID-01",
      bookingId: "BKG-MOCK-101",
      folioId: "FOL-MOCK-201",
      guestName: "Max Mustermann",
      propertyId: propertiesToUse[0],
      departureDate: to,
      folioBalance: 0.00,
      netCityTax: 14.50,
      unpaidCityTax: 0.00,
      currency: "EUR",
      status: "Bezahlt",
      category: "paid"
    });
    mockRecords.push({
      reservationId: "RES-MOCK-PAID-02",
      bookingId: "BKG-MOCK-102",
      folioId: "FOL-MOCK-202",
      guestName: "Sarah Connor",
      propertyId: propertiesToUse[propertiesToUse.length - 1],
      departureDate: from,
      folioBalance: 0.00,
      netCityTax: 8.40,
      unpaidCityTax: 0.00,
      currency: "EUR",
      status: "Bezahlt",
      category: "paid"
    });

    // 2. Ausstehend (Open folios, outstanding city tax)
    mockRecords.push({
      reservationId: "RES-MOCK-UNPAID-01",
      bookingId: "BKG-MOCK-201",
      folioId: "FOL-MOCK-301",
      guestName: "John Doe",
      propertyId: propertiesToUse[0],
      departureDate: to,
      folioBalance: -150.00,
      netCityTax: 12.00,
      unpaidCityTax: 12.00,
      currency: "EUR",
      status: "Ausstehend (Skipper / Invoice)",
      category: "unpaid"
    });
    mockRecords.push({
      reservationId: "RES-MOCK-UNPAID-02",
      bookingId: "BKG-MOCK-202",
      folioId: "FOL-MOCK-302",
      guestName: "Alice Smith",
      propertyId: propertiesToUse[Math.floor(propertiesToUse.length / 2)],
      departureDate: from,
      folioBalance: -5.00,
      netCityTax: 10.00,
      unpaidCityTax: 5.00, // Capped at remaining balance
      currency: "EUR",
      status: "Ausstehend (Teilzahlung)",
      category: "unpaid"
    });

    // 3. Storniert (Allowances offset)
    mockRecords.push({
      reservationId: "RES-MOCK-PAID-01", // Match paid reservation ID to trigger conflict!
      bookingId: "BKG-MOCK-101",
      folioId: "FOL-MOCK-201-EX",
      guestName: "Max Mustermann",
      propertyId: propertiesToUse[0],
      departureDate: to,
      folioBalance: 0.00,
      netCityTax: 0.00,
      unpaidCityTax: 0.00,
      exemptedCityTax: 14.50,
      currency: "EUR",
      status: "Storniert (Gutschrift)",
      category: "exempted",
      allowanceReason: "Nachträgliche Befreiung (Doppelbuchung)"
    });
    mockRecords.push({
      reservationId: "RES-MOCK-ALLOW-01",
      bookingId: "BKG-MOCK-301",
      folioId: "FOL-MOCK-401",
      guestName: "Bob Ross",
      propertyId: propertiesToUse[0],
      departureDate: from,
      folioBalance: 0.00,
      netCityTax: 0.00,
      unpaidCityTax: 0.00,
      exemptedCityTax: 15.00,
      currency: "EUR",
      status: "Storniert (Gutschrift)",
      category: "exempted",
      allowanceReason: "Business-Exempted (Certificate provided)"
    });
    mockRecords.push({
      reservationId: "RES-MOCK-ALLOW-02",
      bookingId: "BKG-MOCK-302",
      folioId: "FOL-MOCK-402",
      guestName: "Charlie Brown",
      propertyId: propertiesToUse[propertiesToUse.length - 1],
      departureDate: to,
      folioBalance: -45.00, // Folio still open for other items
      netCityTax: 0.00,
      unpaidCityTax: 0.00,
      exemptedCityTax: 12.00,
      currency: "EUR",
      status: "Storniert (Gutschrift)",
      category: "exempted",
      allowanceReason: "Schüler/Studenten Befreiung"
    });

    return mockRecords;
  }

  try {
    // 1. Fetch checked-out reservations within the departure window
    const reservations = await this.fetchReservations(from, to, "Departure", "primaryGuest");
    const checkedOutRess = reservations.filter(
      (r) => r.status === "CheckedOut" && (propertyFilter === "All" || r.property?.id === propertyFilter)
    );

    if (checkedOutRess.length === 0) return [];

    const reservationIds = checkedOutRess.map((r) => r.id ?? "");
    const chunkSize = 50;
    const allUnpaidTaxes: CityTaxAuditRecord[] = [];

    // 2. Batch-query folios chunked by 50 reservation IDs, with bounded
    // concurrency (4 chunks in flight): the previous fully-serial loop made
    // wall-clock time = chunks x latency (P.6 fix). Chunks are independent
    // read-only GETs, so parallel fetching is safe; results are appended in
    // original order below.
    const CONCURRENCY = 4;
    const folioResults: Array<{ folios?: FolioModel[] } | null> = [];
    for (let i = 0; i < reservationIds.length; i += chunkSize * CONCURRENCY) {
      const groupChunks = [];
      for (let j = i; j < Math.min(i + chunkSize * CONCURRENCY, reservationIds.length); j += chunkSize) {
        groupChunks.push(reservationIds.slice(j, j + chunkSize));
      }
      const groupData = await Promise.all(groupChunks.map(chunkIds => {
        const resIdParams = `reservationIds=${chunkIds.join(",")}`;
        // Fetch open/closed folios for these reservation IDs with expanded charges & allowances
        return this._fetch(
          `/finance/v1/folios?expand=charges&expand=allowances&${resIdParams}`
        ).catch((err: unknown) => {
          console.warn("Failed to fetch folios chunk:", err);
          return null;
        }) as Promise<{ folios?: FolioModel[] } | null>;
      }));
      folioResults.push(...groupData);
    }

    for (const foliosData of folioResults) {
      if (foliosData && foliosData.folios) {
        for (const folio of foliosData.folios) {
          const folioBalance = Number(folio.balance?.amount || 0);
          const folioStatus = folio.status || "Open";

          // Identify city tax charges (excluding routed-out or moved-out charges)
          const totalCityTaxCharges = (folio.charges || [])
            .filter(
              (c) =>
                c.isPosted &&
                (c.serviceType === "CityTax" || c.serviceType === "SecondCityTax") &&
                !c.routedTo &&
                !c.movedTo
            )
            .reduce((sum, c) => sum + Number(c.amount?.grossAmount || 0), 0);

          // Identify city tax allowances (excluding moved-out allowances)
          const totalCityTaxAllowances = (folio.allowances || [])
            .filter(
              (a) =>
                (a.serviceType === "CityTax" || a.serviceType === "SecondCityTax") &&
                !a.movedTo
            )
            .reduce((sum, a) => sum + Number(a.amount?.grossAmount || 0), 0);

          const netCityTax = totalCityTaxCharges - totalCityTaxAllowances;
          const matchedRes = checkedOutRess.find((r) => r.id === folio.reservation?.id);

          // Classify each case
          if (totalCityTaxCharges > 0 && netCityTax <= 0) {
            // Case 3: Cancelled / Exempted (Rebated via Allowances)
            const firstAllowance = (folio.allowances || []).find(
              (a) => a.serviceType === "CityTax" || a.serviceType === "SecondCityTax"
            );
            allUnpaidTaxes.push({
              reservationId: folio.reservation?.id,
              bookingId: folio.bookingId,
              folioId: folio.id,
              guestName: matchedRes
                ? `${matchedRes.primaryGuest?.firstName || ""} ${matchedRes.primaryGuest?.lastName || ""}`.trim()
                : "Unbekannt",
              propertyId: matchedRes?.property?.id || folio.property?.id,
              departureDate: matchedRes?.departure,
              folioBalance: folioBalance,
              netCityTax: 0.00,
              unpaidCityTax: 0.00,
              exemptedCityTax: Number(totalCityTaxCharges.toFixed(2)),
              currency: folio.balance?.currency || "EUR",
              status: "Storniert (Gutschrift)",
              category: "exempted",
              allowanceReason: firstAllowance?.reason || "Befreiungs-Zertifikat"
            });
          } else if (netCityTax > 0) {
            if (folioStatus === "Closed" || folioBalance >= 0) {
              // Case 1: Bezahlt (Closed / Fully Paid)
              allUnpaidTaxes.push({
                reservationId: folio.reservation?.id,
                bookingId: folio.bookingId,
                folioId: folio.id,
                guestName: matchedRes
                  ? `${matchedRes.primaryGuest?.firstName || ""} ${matchedRes.primaryGuest?.lastName || ""}`.trim()
                  : "Unbekannt",
                propertyId: matchedRes?.property?.id || folio.property?.id,
                departureDate: matchedRes?.departure,
                folioBalance: folioBalance,
                netCityTax: Number(netCityTax.toFixed(2)),
                unpaidCityTax: 0.00,
                currency: folio.balance?.currency || "EUR",
                status: "Bezahlt",
                category: "paid"
              });
            } else {
              // Case 2: Ausstehend (Open & Unpaid)
              const absFolioBalance = Math.abs(folioBalance);
              const unpaidCityTax = Math.max(0, Math.min(netCityTax, absFolioBalance));
              if (unpaidCityTax > 0) {
                allUnpaidTaxes.push({
                  reservationId: folio.reservation?.id,
                  bookingId: folio.bookingId,
                  folioId: folio.id,
                  guestName: matchedRes
                    ? `${matchedRes.primaryGuest?.firstName || ""} ${matchedRes.primaryGuest?.lastName || ""}`.trim()
                    : "Unbekannt",
                  propertyId: matchedRes?.property?.id || folio.property?.id,
                  departureDate: matchedRes?.departure,
                  folioBalance: folioBalance,
                  netCityTax: Number(netCityTax.toFixed(2)),
                  unpaidCityTax: Number(unpaidCityTax.toFixed(2)),
                  currency: folio.balance?.currency || "EUR",
                  status: "Ausstehend",
                  category: "unpaid"
                });
              }
            }
          }
        }
      }
    }

    // Group and aggregate by reservationId + category to prevent duplicate reservation entries in the same tab
    const aggregatedTaxes: CityTaxAuditRecord[] = [];
    const seen: Record<string, CityTaxAuditRecord> = {};

    for (const item of allUnpaidTaxes) {
      const key = `${item.reservationId}_${item.category}`;
      const existing = seen[key];
      if (!existing) {
        seen[key] = { ...item };
        aggregatedTaxes.push(seen[key]);
      } else {
        existing.netCityTax = Number((existing.netCityTax + item.netCityTax).toFixed(2));
        existing.unpaidCityTax = Number((existing.unpaidCityTax + item.unpaidCityTax).toFixed(2));
        existing.exemptedCityTax = Number(((existing.exemptedCityTax || 0) + (item.exemptedCityTax || 0)).toFixed(2));
        existing.folioBalance = Number((existing.folioBalance + item.folioBalance).toFixed(2));
        if (existing.folioId && item.folioId && existing.folioId !== item.folioId) {
          existing.folioId = `${existing.folioId}, ${item.folioId}`;
        }
        if (item.allowanceReason) {
          if (!existing.allowanceReason) {
            existing.allowanceReason = item.allowanceReason;
          } else if (!existing.allowanceReason.includes(item.allowanceReason)) {
            existing.allowanceReason = `${existing.allowanceReason}; ${item.allowanceReason}`;
          }
        }
      }
    }

    return aggregatedTaxes;
  } catch (e) {
    console.error("Error executing fetchCityTaxAuditData:", e);
    throw e;
  }
},

async cleanupBookingNames(from: string, to: string, dryRun = true): Promise<void> {
  console.log(
    `%c[Cleanup] Starting guest name cleanup from ${from} to ${to} (Dry Run: ${dryRun})`,
    "color: #859F4A; font-weight: bold;",
  );
  try {
    const reservations = await this.fetchReservations(from, to, "Arrival");
    if (!reservations || reservations.length === 0) {
      console.log(
        "%c[Cleanup] No reservations found in this date range.",
        "color: orange;",
      );
      return;
    }

    const matches: { res: ReservationModel; newLastName: string }[] = [];
    for (const res of reservations) {
      const g = res.primaryGuest;
      if (!g || !g.firstName || !g.lastName) continue;

      const firstName = g.firstName.trim();
      const lastName = g.lastName.trim();

      // Check if last name starts with first name + space (case-insensitive)
      if (lastName.toLowerCase().startsWith(firstName.toLowerCase() + " ")) {
        const newLastName = lastName.substring(firstName.length).trim();
        matches.push({ res, newLastName });
      }
    }

    if (matches.length === 0) {
      console.log(
        "%c[Cleanup] No reservations with duplicated first/last name pattern found.",
        "color: green; font-weight: bold;",
      );
      return;
    }

    console.log(
      `%c[Cleanup] Found ${matches.length} reservations to correct:`,
      "color: #859F4A; font-weight: bold;",
    );
    for (const match of matches) {
      const { res, newLastName } = match;
      console.log(
        `  - Res-ID: ${res.id} (${res.property?.id || "?"}) | Guest: ${res.primaryGuest?.firstName} | ${res.primaryGuest?.lastName} -> ${newLastName}`,
      );
    }

    if (dryRun) {
      console.log(
        '%c[Cleanup] Dry run finished. No changes were applied. Run with dryRun=false to save: await window.cleanupBookingNames("YYYY-MM-DD", "YYYY-MM-DD", false)',
        "color: #3b82f6; font-weight: bold;",
      );
      return;
    }

    console.log(
      "%c[Cleanup] Applying corrections to Apaleo...",
      "color: red; font-weight: bold;",
    );
    let successCount = 0;
    // Batched PATCHes (P.6 fix): groups of 5 with a 1s pause between batches to
    // stay well under Apaleo rate limits on large matches, instead of one
    // unthrottled serial PATCH per reservation.
    const PATCH_BATCH = 5;
    const BATCH_PAUSE_MS = 1000;
    const patchable = matches.filter(m => {
      const g = m.res.primaryGuest;
      return g && g.firstName;
    });
    for (let i = 0; i < patchable.length; i += PATCH_BATCH) {
      const batch = patchable.slice(i, i + PATCH_BATCH);
      await Promise.all(batch.map(async ({ res, newLastName }) => {
        const g = res.primaryGuest;
        if (!g || !g.firstName) return;

        const payload: Record<string, unknown> = {
          firstName: g.firstName,
          lastName: newLastName,
        };

        if (g.title) payload.title = g.title;
        if (g.gender) payload.gender = g.gender;
        if (g.email) payload.email = g.email;
        if (g.phone) payload.phone = g.phone;
        if (g.nationalityCountryCode)
          payload.nationalityCountryCode = g.nationalityCountryCode;
        if (g.birthDate) payload.birthDate = g.birthDate;
        if (g.address) payload.address = g.address;
        if (g.company) payload.company = g.company;

        try {
          await this.updateReservation(res.id || "", [
            { op: "replace", path: "/primaryGuest", value: payload },
          ]);
          console.log(
            `%c  [✓] Successfully updated Reservation ${res.id}`,
            "color: green;",
          );
          successCount++;
        } catch (err) {
          console.error(`  [✗] Failed to update Reservation ${res.id}:`, err);
        }
      }));
      if (i + PATCH_BATCH < patchable.length) {
        await new Promise(r => setTimeout(r, BATCH_PAUSE_MS));
      }
    }

    console.log(
      `%c[Cleanup] Done! Successfully corrected ${successCount} of ${matches.length} reservations.`,
      "color: green; font-weight: bold;",
    );
  } catch (error) {
    console.error("[Cleanup] Error during execution:", error);
  }
},

async removeReservationCityTax(reservationId: string): Promise<unknown> {
  if (!reservationId) throw new Error("removeReservationCityTax: reservationId is required");
  return await this._fetch(
    `/booking/v1/reservation-actions/${reservationId}/remove-city-tax`,
    { method: "PUT" },
  );
},

};

export default part;
